import Phaser from 'phaser';
import { addPixelText } from '../pixelText';
import { FONT_UI, UI, hexColor } from '../theme';
import { KIT_STATES, kitTextureKey } from './manifest';
import { addKitImage, addKitNineSlice } from './nineSlice';

export type PanelVariant = keyof typeof KIT_STATES.panel;
export type ButtonState = 'normal' | 'selected' | 'disabled' | 'alt';
export type SlotState = 'empty' | 'normal' | 'selected' | 'equipped' | 'disabled';
export type ProgressFill = 'blue' | 'red' | 'green';

/** Nine-slice panel frame. Position is top-left of the panel. */
export function addUiPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  variant: PanelVariant = 'ornate',
): Phaser.GameObjects.Container {
  const key = KIT_STATES.panel[variant];
  const frame = addKitNineSlice(scene, key, 0, 0, w, h);
  return scene.add.container(x, y, [frame]);
}

/**
 * Wide chrome button with optional crest overlay for selected state.
 * (x,y) is the center of the button body.
 */
export function addUiButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  onClick: () => void,
  options: {
    width?: number;
    height?: number;
    state?: ButtonState;
    fontSize?: number;
  } = {},
): Phaser.GameObjects.Container {
  const width = options.width ?? 160;
  const height = options.height ?? 36;
  const state = options.state ?? 'normal';
  const frameKey =
    state === 'selected'
      ? KIT_STATES.button.selected
      : state === 'disabled'
        ? KIT_STATES.button.disabled
        : state === 'alt'
          ? KIT_STATES.button.alt
          : KIT_STATES.button.normal;

  const frame = addKitNineSlice(scene, frameKey, -width / 2, -height / 2, width, height);
  const children: Phaser.GameObjects.GameObject[] = [frame];

  if (state === 'selected') {
    const crest = addKitImage(scene, KIT_STATES.button.selectedOverlay, 0, -height / 2 - 2, {
      originX: 0.5,
      originY: 1,
    });
    children.push(crest);
  }

  const text = scene.add
    .text(0, 0, label, {
      fontFamily: FONT_UI,
      fontSize: `${options.fontSize ?? (label.length > 18 ? 12 : 14)}px`,
      color: hexColor(state === 'disabled' ? UI.textMuted : UI.parchment),
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: width - 16 },
    })
    .setOrigin(0.5);
  children.push(text);

  const childrenWithHit: Phaser.GameObjects.GameObject[] = [...children];
  const container = scene.add.container(x, y, childrenWithHit);
  if (state === 'disabled') {
    container.setAlpha(0.55);
  } else {
    const hit = scene.add
      .rectangle(0, 0, width, height, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    container.add(hit);
    hit.on('pointerup', onClick);
  }
  return container;
}

/**
 * Equipment / inventory slot with separate crest overlay for selected/equipped.
 * (x,y) is the center of the slot.
 */
export function addUiSlot(
  scene: Phaser.Scene,
  x: number,
  y: number,
  size: number,
  state: SlotState = 'empty',
): Phaser.GameObjects.Container {
  const frameKey =
    state === 'selected' || state === 'equipped'
      ? KIT_STATES.slot.selected
      : state === 'normal'
        ? KIT_STATES.slot.normal
        : KIT_STATES.slot.empty;

  const frame = addKitNineSlice(scene, frameKey, -size / 2, -size / 2, size, size);
  const children: Phaser.GameObjects.GameObject[] = [frame];

  if (state === 'selected' || state === 'equipped') {
    const crest = addKitImage(scene, KIT_STATES.slot.selectedOverlay, 0, -size / 2 + 2, {
      originX: 0.5,
      originY: 1,
    });
    children.push(crest);
  }

  if (state === 'disabled') {
    const veil = scene.add.rectangle(0, 0, size, size, 0x000000, 0.45);
    children.push(veil);
  }

  return scene.add.container(x, y, children);
}

/**
 * Horizontal progress bar: kit frame + clipped fill strip.
 * (x,y) is top-left of the bar.
 */
export function addUiProgressBar(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  ratio: number,
  fill: ProgressFill = 'green',
): Phaser.GameObjects.Container {
  const frameKey = KIT_STATES.progress.frame;
  const fillKey =
    fill === 'blue'
      ? KIT_STATES.progress.fillBlue
      : fill === 'red'
        ? KIT_STATES.progress.fillRed
        : KIT_STATES.progress.fillGreen;

  const frame = addKitNineSlice(scene, frameKey, 0, 0, width, height);
  const padX = 10;
  const padY = Math.max(1, Math.floor((height - 4) / 2));
  const innerW = Math.max(2, width - padX * 2);
  const innerH = Math.max(2, height - padY * 2);
  const fillW = Math.max(2, Math.floor(innerW * Phaser.Math.Clamp(ratio, 0, 1)));

  const fillTex = scene.textures.exists(kitTextureKey(fillKey))
    ? kitTextureKey(fillKey)
    : 'ui-slot-empty';
  const fillImg = scene.add.image(padX, padY, fillTex);
  fillImg.setOrigin(0, 0);
  fillImg.setDisplaySize(fillW, innerH);

  return scene.add.container(x, y, [frame, fillImg]);
}

/** Nameplate / section header with crest overlay. Centered at (x,y). */
export function addUiNameplate(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  label: string,
): Phaser.GameObjects.Container {
  const height = 22;
  const body = addKitNineSlice(
    scene,
    KIT_STATES.nameplate.body,
    -width / 2,
    -height / 2,
    width,
    height,
  );
  const crest = addKitImage(scene, KIT_STATES.nameplate.crest, 0, -height / 2 - 1, {
    originX: 0.5,
    originY: 1,
  });
  const text = addPixelText(scene, 0, -6, label, {
    font: 'ui-yellow',
    size: 12,
    originX: 0.5,
  });
  return scene.add.container(x, y, [body, crest, text]);
}

/**
 * Nav tab chrome using kit tab frames + crest overlay when active.
 * (x,y) center; returns container (caller adds icon/label/hit separately if needed).
 */
export function addUiTabFrame(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  active: boolean,
): Phaser.GameObjects.Container {
  const key = active ? KIT_STATES.tab.selected : KIT_STATES.tab.normal;
  const frame = addKitNineSlice(scene, key, -width / 2, -height / 2, width, height);
  const children: Phaser.GameObjects.GameObject[] = [frame];
  if (active) {
    children.push(
      addKitImage(scene, KIT_STATES.tab.selectedOverlay, 0, -height / 2 - 1, {
        originX: 0.5,
        originY: 1,
      }),
    );
  }
  return scene.add.container(x, y, children);
}
